import React, { useState, useEffect, useCallback } from "react";

import { useHistory, useRouteMatch } from "react-router-dom";

import { Organization } from "modules/api/endpoints/organizationGet";

import { api, API } from "modules/api";
import { AssessmentGetResponse } from "modules/api/endpoints/assessmentGet";

import { ErrorMessagesType, assessmentErrorMessages } from "modules/messages";

import styles from "./styles.module.scss";

import Template from "./Template";

import { useModal } from "modules/hooks/modal";
import { ButtonStyle } from "components/Button";
import UseLockAssessment from "../hooks/useLockAssessment";
import { getErrorMessages } from "modules/messages/helpers";
import { unarchiveAssessment } from "modules/utils/ArchiveAssessments";
import MetaTitle from "components/MetaTitle";

type questiosnParam = {
  assessmentId: string;
};

type AssessmentResponse = {
  id: number;
  perspectives: Items[];
  threads: Items[];
  selectedLevel: number;
  organization: Organization;
  name: string;
  certified: boolean;
  selectedScope: string;
  status: string;
  isArchived: boolean;
};

export type Items = {
  id: number;
  code: string;
  name: string;
  isSelected: boolean;
};

type PerspectiveProgressItem = {
  id: number;
  name: string;
  currentScore: number;
  totalThreads: number;
  answeredThreads: number;
  threads: ProgressItem[];
};

export type ProgressItem = {
  id: number;
  descoped: boolean;
  name: string;
  answered: boolean;
  code: string;
  started: boolean;
  score: number;
};

export type ProgressType = {
  id: number;
  name: string;
  currentOverallScore: number;
  maturityScore: number;
  perspectivesProgress: PerspectiveProgressItem[];
};

export type ProgressFEType = {
  id: number;
  perspective: string;
  code: string;
  answeredThreads: number;
  threads: ProgressItem[];
  currentScore: number;
  totalTreads: number;
};

const QuestionsSummary: React.FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [assessment, setAssessment] = useState<AssessmentResponse>(
    {} as AssessmentResponse
  );
  const [progress, setProgress] = useState<ProgressType>({} as ProgressType);
  const [isAssessmentSubmitable, setIsAssessmentSubmitable] = useState(false);
  const [isSubmitingAssessment, setIsSubmitingAssessment] = useState(false);

  const submitConfirmation = useModal();

  const {
    params: { assessmentId },
  } = useRouteMatch<questiosnParam>();
  const history = useHistory();

  const handleNavegate = (perspective: string, thread: string) => {
    const perspectiveCode = assessment.perspectives.filter(
      (p) => p.name === perspective
    )[0].code;

    const threadCode = assessment.threads.filter((t) => t.name === thread)[0]
      .code;

    if (perspectiveCode && threadCode)
      history.push(
        `/assessments/${assessment.id}/questions/${perspectiveCode}/${threadCode}`
      );
  };

  const handleCertify = () => {
    history.push(`/assessments/${assessment.id}/profile`);
  };
  const token = sessionStorage.getItem("@P3M3:token");
  // Get User Session Token
  let parsedToken = null;
  if (token) {
    parsedToken = JSON.parse(token);
  }
  const data = { authorization: parsedToken.token };
  const url = API.POST_DELETE_ASSESSMENT_LOCK(Number(assessmentId)).url;
  // Set request content type
  const headers = {
    type: "application/json",
  };
  // set request data
  const blob = new Blob([JSON.stringify(data)], headers);
  // Setup beacon request
  const eventHandler = () => {
    sessionStorage.removeItem(`@P3M3:lock:${assessmentId}`);
    navigator.sendBeacon(url, blob);
  };
  // Check if assessment has been locked already
  const handleIsLocked = async (id: number) => {
    try {
      const response = await api(API.POST_ASSESSMENT_LOCK(id)).then(
        (res) => res.data
      );
      if (response) {
        return true;
      }
    } catch (error: any) {
      if (error.response.status === 409) {
        history.push("/");
        sessionStorage.removeItem(`@P3M3:lock:${assessmentId}`);
        return false;
      }
    }
    return false;
  };
  // Handle user lock
  const checkLockSession = async () => {
    if (sessionStorage.getItem(`@P3M3:lock:${assessmentId}`) === null) {
      sessionStorage.setItem(`@P3M3:lock:${assessmentId}`, "1");
      const response = await handleIsLocked(Number(assessmentId));
      if (response) {
        return true;
      }
    }
  };

  useEffect(() => {
    checkLockSession();
    window.addEventListener("beforeunload", eventHandler, true);
    return () => {
      window.removeEventListener("beforeunload", eventHandler, true);
    };
  }, []);
  useEffect(() => {
    let isCancelled = false;

    if (!isAssessmentValid(assessmentId)) {
      return history.push("/");
    }

    const fetchAssessment = async () => {
      try {
        const [assessmentProgress, assessmentData] = await Promise.all([
          api(API.GET_ASSESSMENT_PROGRESS(assessmentId)),
          api(API.GET_ASSESSMENT(assessmentId)).then(
            (res: AssessmentGetResponse) => res.data
          ),
        ]);

        if (!assessmentData && isCancelled) history.push("/");

        const isSubmitable = assessmentProgress.data.perspectivesProgress.every(
          (p: PerspectiveProgressItem) => {
            const descopedThreads = p.threads.filter((t) => t.descoped);
            if (p.answeredThreads === 0) return false;
            return (
              p.answeredThreads === p.totalThreads - descopedThreads.length
            );
          }
        );

        setIsAssessmentSubmitable(isSubmitable);
        setProgress(assessmentProgress.data);
        setAssessment(assessmentData);
        setIsLoading(false);
      } catch (error: any) {
        const messages = getErrorMessages(
          assessmentErrorMessages,
          error?.response?.status
        );
        await submitConfirmation({
          type: "error",
          catchOnCancel: true,
          title: messages.title,
          description: messages.description,
        }).finally(() => apiErrorRedirection(error?.response?.status));
      }
    };

    fetchAssessment();

    return () => {
      isCancelled = true;
      const unlockAssessment = UseLockAssessment(
        history.location.pathname,
        Number(assessmentId)
      );
      unlockAssessment();
    };
  }, []);

  const apiErrorRedirection = (error = 400) => {
    if (error && error === 401) {
      window.location.assign("https://www.axelos.com/");
      return;
    }
    history.push("/");
  };

  const isAssessmentValid = useCallback((assemt: string) => {
    return !!assemt;
  }, []);

  const getTreadStatus = useCallback((threadItem: ProgressItem) => {
    if (threadItem.descoped) return "";
    if (!threadItem.started) return styles.notAnswered;
    if (threadItem.answered) return styles.answered;
    return styles.warning;
  }, []);
  const handleSubmitAssessment = async () => {
    setIsSubmitingAssessment(true);
    try {
      const confirmationResponse = await submitConfirmation({
        type: "info",
        catchOnCancel: true,
        title: "Are you ready to Submit?",
        description:
          "Once you have submitted this assessment you cannot make any changes but will have access to a read-only copy.",
        buttonLabel: "Submit assessment",
        displayCancelButton: true,
      })
        .then(() => true)
        .catch(() => {
          setIsSubmitingAssessment(false);
          return false;
        });

      if (!confirmationResponse) return;

      const isSubmitted = await api(API.POST_ASSESSMENT_COMPLETE(assessmentId));
      if (isSubmitted) {
        await submitConfirmation({
          type: "success",
          title: "You will now view the results of this assessment.",
          description: assessment.certified
            ? "A request for certification has been sent out and you will receive the response shortly."
            : "",
          buttonLabel: "Continue",
          buttonStyle: ButtonStyle.SECONDARY,
        });
        UseLockAssessment(history.location.pathname, Number(assessmentId));
        history.push(`/assessments/${assessmentId}/result`);
      }
    } catch (error) {
      submitConfirmation({
        type: "error",
        title: "Something happened, please try again...",
      });

      setIsSubmitingAssessment(false);
    }
  };

  const handleUnarchive = async () => {
    await unarchiveAssessment(assessment.id);
    history.push(`/`);
  };

  return (
    <React.Fragment>
      <MetaTitle title="Assessment Progress Summary | Maturity Assessment Tool" />
      <Template
        isLoading={isLoading}
        isArchived={assessment.isArchived}
        perspectives={assessment.perspectives}
        threads={assessment.threads}
        organization={assessment.organization}
        scope={assessment.selectedScope}
        name={assessment.name}
        displayCertifyAssessment={!assessment.certified}
        handleNavegate={handleNavegate}
        handleCertify={handleCertify}
        progress={progress}
        isAssessmentSubmitable={isAssessmentSubmitable}
        isSubmitingAssessment={isSubmitingAssessment}
        handleSubmitAssessment={handleSubmitAssessment}
        isAssessmentLocked={assessment.status}
        handleThreadStatus={getTreadStatus}
        handleUnarchive={handleUnarchive}
      />
    </React.Fragment>
  );
};

export default QuestionsSummary;
